import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, KeyRound, Smartphone } from "lucide-react";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { translateAuthError } from '../lib/authErrors';

type Step = "start" | "scan" | "done";

/**
 * Registrazione obbligatoria della 2FA (TOTP) per un account admin.
 * L'utente arriva qui solo se è già verificato come admin (admin_users)
 * ma non ha ancora un fattore MFA registrato - vedi AdminRoute.tsx.
 */
export default function AdminMfaSetup() {
	const navigate = useNavigate();
	const [step, setStep] = useState<Step>("start");
	const [qrCode, setQrCode] = useState<string | null>(null);
	const [secret, setSecret] = useState<string | null>(null);
	const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
	const [factorId, setFactorId] = useState<string | null>(null);
	const [code, setCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const startEnrollment = async () => {
		setError(null);
		setLoading(true);
		try {
			const { data, error: enrollError } = await supabase.auth.mfa.enroll({
				factorType: "totp",
			});
			if (enrollError) throw enrollError;

			setFactorId(data.id);
			setQrCode(data.totp.qr_code);
			setSecret(data.totp.secret);
			setOtpauthUri(data.totp.uri);
			setStep("scan");
		} catch (err: any) {
			setError(translateAuthError(err?.message));
		} finally {
			setLoading(false);
		}
	};

	const confirmEnrollment = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!factorId) return;

		setError(null);
		if (!/^\d{6}$/.test(code)) {
			setError("Inserisci il codice a 6 cifre dalla tua app authenticator");
			return;
		}

		setLoading(true);
		try {
			const { data: challengeData, error: challengeError } =
				await supabase.auth.mfa.challenge({ factorId });
			if (challengeError) throw challengeError;

			const { error: verifyError } = await supabase.auth.mfa.verify({
				factorId,
				challengeId: challengeData.id,
				code,
			});
			if (verifyError) throw verifyError;

			setStep("done");
			setTimeout(() => navigate("/admin/dashboard"), 1200);
		} catch (err: any) {
			setError(translateAuthError(err?.message));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="container max-w-md mx-auto py-12">
			<div className="bg-card p-8 rounded-lg shadow-md">
				<div className="flex items-center gap-2 mb-2">
					<ShieldCheck className="h-6 w-6 text-primary" />
					<h1 className="text-2xl font-bold text-foreground">
						Attiva la verifica in due passaggi
					</h1>
				</div>
				<p className="text-sm text-muted-foreground mb-6">
					Per entrare nel pannello serve un codice di sicurezza generato dal
					telefono, oltre alla password. Si configura una volta sola: poi
					il telefono lo propone da solo quando serve.
				</p>

				{error && (
					<div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
						<p className="text-sm text-red-800 dark:text-red-200">{error}</p>
					</div>
				)}

				{step === "start" && (
					<Button onClick={startEnrollment} disabled={loading} className="w-full">
						{loading ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							"Inizia la configurazione"
						)}
					</Button>
				)}

				{step === "scan" && qrCode && (
					<form onSubmit={confirmEnrollment} className="space-y-4">
						{otpauthUri && (
							<div className="space-y-2">
								<p className="text-sm text-foreground/80">
									<strong>1.</strong> Se sei sul telefono che userai per entrare,
									tocca il pulsante: si apre l&apos;app Password (iPhone) o
									Google Authenticator (Android). Conferma l&apos;aggiunta.
								</p>
								<Button asChild variant="outline" className="w-full">
									<a href={otpauthUri}>
										<Smartphone className="h-4 w-4" />
										Aggiungi a questo telefono
									</a>
								</Button>
								<p className="text-xs text-muted-foreground">
									Sei al computer? Inquadra invece il QR code con la fotocamera
									del telefono.
								</p>
							</div>
						)}

						<div className="flex justify-center bg-white p-4 rounded-lg border border-border">
							<img src={qrCode} alt="QR code 2FA" className="h-48 w-48" />
						</div>

						{secret && (
							<div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
								<KeyRound className="h-3.5 w-3.5" />
								<span>
									Codice manuale: <code className="font-mono">{secret}</code>
								</span>
							</div>
						)}

						<p className="text-sm text-foreground/80">
							<strong>2.</strong> Torna qui e scrivi il codice di 6 cifre che
							vedi nell&apos;app.
						</p>

						<Input
							inputMode="numeric"
							autoComplete="one-time-code"
							maxLength={6}
							placeholder="123456"
							value={code}
							onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
							className="text-center text-lg tracking-widest"
							autoFocus
						/>

						<Button type="submit" disabled={loading} className="w-full">
							{loading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								"Conferma e attiva"
							)}
						</Button>
					</form>
				)}

				{step === "done" && (
					<div className="flex items-center gap-2 text-green-700 dark:text-green-300">
						<ShieldCheck className="h-5 w-5" />
						<p>2FA attivata! Ti sto reindirizzando al pannello...</p>
					</div>
				)}
			</div>
		</div>
	);
}
